import { RefObject, useEffect, useRef, useState } from 'react';

import { UseOnScreenOptions } from './types';

/**
 * Custom hook to detect when an element is visible on the screen (in the viewport).
 *
 * @param options - IntersectionObserver options (root, rootMargin, threshold)
 * @returns An array containing a ref to be attached to the element and a boolean indicating if the element is in the viewport
 */
function useOnScreen(
  options: UseOnScreenOptions,
  alwaysVisible = false,
): [RefObject<HTMLDivElement>, boolean] {
  const ref = useRef<HTMLDivElement>(null);

  const [isVisible, setIsVisible] = useState<boolean>(alwaysVisible);

  useEffect(() => {
    setIsVisible(alwaysVisible);
  }, [alwaysVisible]);

  useEffect(() => {
    if (alwaysVisible) return;
    const currentRef = ref.current;

    // Create an IntersectionObserver instance to observe when the element enters/leaves the viewport
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, options);

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options, alwaysVisible]);

  return [ref, isVisible];
}

export default useOnScreen;
